import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Order } from '@octave/contracts';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Trash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getOrderStatusBadgeClass, getOrderStatusLabel } from '@/lib/order-status';
import { formatPrice } from '@/lib/format';

// Create a separate component for the clickable order ID
function OrderNumberCell({
  orderId,
  orderNumber,
}: {
  orderId: string;
  orderNumber: string;
}) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/o/admin/orders/${orderId}`)}
      className="text-primary hover:underline"
    >
      {orderNumber}
    </button>
  );
}

interface ColumnsProps {
  onDelete: (order: Order) => void;
}

export const createColumns = ({
  onDelete,
}: ColumnsProps): ColumnDef<Order>[] => [
  {
    accessorKey: 'orderNumber',
    header: 'Référence',
    cell: ({ row }) => {
      const orderNumber = row.getValue('orderNumber') as string;
      const orderId = row.original.id;
      return <OrderNumberCell orderId={orderId} orderNumber={orderNumber} />;
    },
  },
  {
    accessorKey: 'organization.name',
    header: 'Organisation',
    cell: ({ row }) => {
      const organizationName = row.original.organization.name;
      return (
        <span className="font-medium text-gray-900">{organizationName}</span>
      );
    },
  },
  {
    accessorKey: 'createdBy',
    header: 'Client',
    cell: ({ row }) => {
      const createdBy = row.getValue('createdBy') as Order['createdBy'];
      if (!createdBy) {
        return <span className="text-muted-foreground">-</span>;
      }
      const firstName = createdBy.firstName || '';
      const lastName = createdBy.lastName || '';
      if (!firstName && !lastName) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <div className="flex flex-col">
          {firstName && <span>{firstName}</span>}
          {lastName && <span>{lastName}</span>}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Statut',
    cell: ({ row }) => {
      const status = row.getValue('status') as string;

      return (
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm ${getOrderStatusBadgeClass(status)}`}
        >
          {getOrderStatusLabel(status)}
        </span>
      );
    },
  },
  {
    accessorKey: 'items',
    header: 'Articles',
    cell: ({ row }) => {
      const items = row.getValue('items') as Order['items'];
      const displayItems = items.slice(0, 4);
      const remainingCount = items.length - 4;

      return (
        <div className="flex flex-wrap gap-2">
          {displayItems.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 text-gray-800 px-3 py-1 text-xs font-medium shadow-sm hover:bg-gray-200 transition"
            >
              {item.quantity > 1 ? `${item.quantity}x ` : ''}
              {item.name}
            </span>
          ))}
          {remainingCount > 0 && (
            <span
              className="inline-flex items-center justify-center rounded-full border border-gray-400 bg-gray-200 text-gray-700 px-2 py-1 text-xs font-semibold shadow hover:bg-gray-300 transition"
              title={`+${remainingCount} more`}
            >
              <MoreHorizontal className="h-4 w-4 mr-1" />+{remainingCount}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'total',
    header: 'Total',
    cell: ({ row }) => {
      const items = row.getValue('items') as Order['items'];
      const total = items.reduce((sum, item) => sum + item.total, 0);
      const fixedTotal = items
        .filter((item) => item.isFixed)
        .reduce((sum, item) => sum + item.total, 0);
      const recurringTotal = items
        .filter((item) => item.isRecurring)
        .reduce((sum, item) => sum + item.total, 0);

      return (
        <div className="text-right space-y-1">
          {/* Total à la commande */}
          <div className="font-semibold text-base">
            {formatPrice(total)}
          </div>

          {/* Détails des prix */}
          <div className="text-xs space-y-0.5">
            {fixedTotal > 0 && (
              <div className="text-muted-foreground">
                HT (à l'acte):{' '}
                {formatPrice(fixedTotal)}
              </div>
            )}
            {recurringTotal > 0 && (
              <div className="text-blue-600 font-medium">
                HT/mois:{' '}
                {formatPrice(recurringTotal)}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: ({ row }) => {
      return format(new Date(row.getValue('createdAt')), 'dd/MM/yyyy - HH:mm');
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const order = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Ouvrir le menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => onDelete(order)}
            >
              <Trash className="mr-2 h-4 w-4" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

